import * as yup from 'yup';
import { CreatorApplicationFormData } from '@/store/useCreatorApplicationStore';
import {
  MAX_CHARS_IN_BIO,
  MIN_CHARS_IN_BIO,
  READINESS_DOMAIN,
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_DOMAINS_LIST,
} from '@/constants';
import { ContentFocusValue } from './ContentFocusTile';
import { getCreatorSocialUsername } from './creatorSocialLinks';

export const contentFocusOptions: Array<{ label: string; value: ContentFocusValue }> = [
  ...WELLNESS_DOMAINS_LIST.map((domain) => ({
    label: WELLNESS_DOMAINS_DISPLAY_LABELS[domain],
    value: domain,
  })),
  { label: 'Readiness', value: READINESS_DOMAIN },
];

const validateSocialMediaHandle = (handle: string | undefined, context: yup.TestContext) => {
  const trimmedHandle = handle?.trim() ?? '';
  if (!trimmedHandle) return true;

  const index = Number(context.path.match(/\[(\d+)\]/)?.[1]);
  const socialMediaUsername = getCreatorSocialUsername(trimmedHandle, index);
  const isValid = socialMediaUsername.length >= 2 && !/[\s/]/.test(socialMediaUsername);

  return (
    isValid ||
    context.createError({
      message: 'Enter a valid username.',
    })
  );
};

const hasAtLeastOneSocialMediaHandle = (handles?: unknown) =>
  Array.isArray(handles) && handles.some((handle) => typeof handle === 'string' && Boolean(handle.trim()));

const hasAnyLocationField = ({ city, zipCode, state }: Partial<CreatorApplicationFormData>) =>
  Boolean(city?.trim() || zipCode?.trim() || state?.trim());

export const creatorApplicationSchema: yup.ObjectSchema<CreatorApplicationFormData> = yup.object({
  creatorName: yup
    .string()
    .trim()
    .when('keepNameSameAsRealName', {
      is: false,
      then: (schema) => schema.required('Creator name is required.'),
      otherwise: (schema) => schema.optional(),
    }),
  keepNameSameAsRealName: yup.boolean().optional(),
  keepPhotoSameAsMemberPhoto: yup.boolean().optional(),
  profilePhoto: yup
    .mixed<File>()
    .nullable()
    .defined()
    .test('profile-photo-required', 'Profile photo is required.', function validateProfilePhoto(file) {
      return Boolean(this.parent.keepPhotoSameAsMemberPhoto || file || this.parent.existingProfilePhotoUrl);
    }),
  existingProfilePhotoUrl: yup.string().nullable().optional(),
  aboutMe: yup
    .string()
    .required('About Me is required.')
    .min(MIN_CHARS_IN_BIO, `Minimum ${MIN_CHARS_IN_BIO} characters required in Bio`)
    .max(MAX_CHARS_IN_BIO, `Maximum ${MAX_CHARS_IN_BIO} characters allowed in Bio`),
  city: yup
    .string()
    .optional()
    .when('$isExternalApplicant', {
      is: true,
      then: (schema) => schema.trim().required('City is required.'),
    })
    .test('complete-location-city', 'City is required when location is entered.', function validateCity(value) {
      return !hasAnyLocationField(this.parent) || Boolean(value?.trim());
    }),
  zipCode: yup
    .string()
    .optional()
    .when('$isExternalApplicant', {
      is: true,
      then: (schema) => schema.trim().required('ZIP Code is required.'),
    })
    .test(
      'complete-location-zip-code',
      'ZIP Code is required when location is entered.',
      function validateZipCode(value) {
        return !hasAnyLocationField(this.parent) || Boolean(value?.trim());
      },
    )
    .test('valid-location-zip-code', 'ZIP Code must be exactly 5 digits.', (value) => {
      const trimmedValue = value?.trim();

      return !trimmedValue || /^\d{5}$/.test(trimmedValue);
    }),
  state: yup
    .string()
    .optional()
    .when('$isExternalApplicant', {
      is: true,
      then: (schema) => schema.trim().required('State is required.'),
    })
    .test('complete-location-state', 'State is required when location is entered.', function validateState(value) {
      return !hasAnyLocationField(this.parent) || Boolean(value?.trim());
    }),
  socialMediaHandles: yup
    .array()
    .of(
      yup
        .string()
        .defined()
        .test('social-media-handle', 'Invalid social media handle.', function validateHandle(value) {
          return validateSocialMediaHandle(value, this);
        }),
    )
    .test('social-media-handle-required', 'Add at least one social media handle.', hasAtLeastOneSocialMediaHandle)
    .defined(),
  qualifications: yup.array().of(yup.string().defined()).defined(),
  contentFocus: yup
    .array()
    .of(
      yup
        .mixed<ContentFocusValue>()
        .oneOf(contentFocusOptions.map((option) => option.value))
        .defined(),
    )
    .min(1, 'Select at least one content focus.')
    .defined(),
});
